import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { ListItem } from 'material-ui/List';
import ContextIconMenu from '../Common/ContextIconMenu';
import EditIcon from 'material-ui/svg-icons/editor/mode-edit';
import IconButton from 'material-ui/IconButton';
import InfoIcon from 'material-ui/svg-icons/action/info';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import RestoreIcon from 'material-ui/svg-icons/action/restore';
import SettingsIcon from 'material-ui/svg-icons/action/settings';
import SpotIcon from 'material-ui/svg-icons/image/lens';
import UndoIcon from 'material-ui/svg-icons/content/undo';

import LinearProgress from 'material-ui/LinearProgress';
import Gravatar from 'react-gravatar';

import * as Colors from 'material-ui/styles/colors';

import UserActions from '../../actions/UserActionCreator';
import { sanitise } from '../../stores/UserStore';

import ROLES from '../../constants/Roles';
import { statusColours } from '../../constants/ColourConstants';
import { formatName } from '../../utils/StringUtils';

const getActions = (row, restoreDeletedAction, restoreArchivedAction) => {
    if (row.sysStatus === 1) {
        return !restoreDeletedAction ? [] : [{
            icon: <UndoIcon />,
            label: 'Undo',
            action: restoreDeletedAction,
            condition: {
                roles: [ROLES.ADMIN]
            }
        }];
    }
    if (!!row.archived) {
        return !restoreArchivedAction ? [] : [
            {
                link: `/dashboard/user/${row.id}`,
                label: 'Preview',
                icon: <InfoIcon/>
            },
            {type: 'divider'},
            {
                icon: <RestoreIcon />,
                label: 'Restore',
                action: restoreArchivedAction,
                condition: {
                    roles: [ROLES.ADMIN]
                }
            }
        ];
    }

    return ([
        {
            link: `/dashboard/user/${row.id}`,
            label: 'Preview',
            icon: <InfoIcon/>
        },
        {type: 'divider'},
        {
            link: `/dashboard/user/${row.id}/settings`,
            label: 'Settings',
            icon: <SettingsIcon/>,
            condition: {
                roles: [ROLES.ADMIN]
            }
        },
        {
            link: `/dashboard/user/${row.id}/edit`,
            label: 'Edit',
            icon: <EditIcon/>,
            condition: {
                roles: [ROLES.ADMIN]
            }
        },
        {
            type: 'deleteDialog',
            dialogType: 'menu',
            value: row.id,
            title: 'Delete user',
            deleteAction: UserActions.delete,
            message: (<span>Are you sure to delete the user "{formatName(row)}" ?</span>),
            condition: {
                roles: [ROLES.ADMIN]
            }
        }
    ]);
};

const style = {
    menu: {
        marginRight: 0,
        marginBottom: 0,
        float: 'left',
        position: 'relative',
        zIndex: 0,
    },
    menuItem: {
        color: '#CC0000',
    },
    iconButton: {
        marginRight: 0,
    },
    listItem: {
        padding: '2px',
    },
    avatar: {
        borderRadius: '50%',
        marginRight: '16px',
    },
    inactive: {
        color: statusColours.deleted
    }
};

const UserListItem = (props) => {

    const row = sanitise(props.data);
    const name = formatName(row);
    const deleted = row.sysStatus === 1;
    const infoLink = `/dashboard/user/${row.id}`;

    const restoreDeletedAction = !!props.onRestoreDeleted ? () => props.onRestoreDeleted(row.id, name) : null;
    const restoreArchivedAction = !!props.onRestoreArchived ? () => props.onRestoreArchived(row.id, name) : null;
    const actions = (props.actions || getActions(row, restoreDeletedAction, restoreArchivedAction));

    const description = row.username || '';

    let listItemProps = {
        key: row.id,
        style: style.listItem,

    };

    let flags = [];
    if (!row.enabled) {
        flags.push(<SpotIcon className="icon-small-li" color="#B71B1C"/>);
    } else if (!row.dateWelcomeSent) {
        flags.push(<SpotIcon className="icon-small-li" color="rgba(117, 117, 117, 0.6)"/>);
    }
    if ((!!row.role) && (row.role !== '')) {
        if (row.isAdmin) {
            flags.push(<span key="a" className="flag-text">admin</span>);
        }
        if (row.isManager) {
            flags.push(<span key="m" className="flag-text">manager</span>);
        }
    }

    listItemProps.primaryText = !deleted
        ? <Link to={infoLink} style={!!row.archived ? style.inactive : null} className="primaryText" >{name}</Link>
        : <div style={style.inactive}>{name}</div>;

    if (row.loading) {
        listItemProps.secondaryText = <div><LinearProgress mode="indeterminate"/></div>;
    } else {
        const secondaryText = (
            <div>
                {flags}{description}
                <br />
                <em>{row.email}</em>
            </div>
        );
        listItemProps.secondaryText = !deleted
            ? <Link to={infoLink} style={!!row.archived ? style.inactive : null} className="secondaryText">{secondaryText}</Link>
            : <div style={style.inactive}>{secondaryText}</div>;
        listItemProps.secondaryTextLines = 2;
    }

    const topRight = {horizontal: 'right', vertical: 'top'};
    const iconMenuProps = {
        actions,
        menuStyle: style.menu,
        anchorOrigin: topRight,
        targetOrigin: topRight,
        iconButtonElement:
            <IconButton touch={true} style={style.iconButton} >
                <MoreVertIcon color={Colors.grey400} style={style.iconButton} />
            </IconButton>
    };

    listItemProps.rightIcon = <ContextIconMenu {...iconMenuProps} />;

    return (
        <ListItem
            {...listItemProps}
            leftAvatar={
                <Link to={infoLink} >
                    <Gravatar
                        email={row.email || ''}
                        default='https://cdn.premierit.com/images/icons/contact.png'
                        size={50}
                        style={style.avatar}
                    />
                </Link>
            }
        />
    );
};

UserListItem.contextTypes = {
    auth: PropTypes.object.isRequired
};

export default UserListItem;
